import React, { ReactElement, useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';

import { useTranslation } from 'react-i18next';
import Header from '../../layout/Header';
import Waffle from '../../buttons/Waffle';
import Logo from '../../layout/Logo/Logo';
import Main from '../../layout/Main';
import StoreLocation from '../StoreLocation';
import OrderItem from './OrderItem';
import SlideUpFooter from '../../layout/SlideUpFooter';
import Footer from '../../layout/Footer';
import Button from '../../buttons/Button';
import IconButton from '../../buttons/IconButton';
import ArrowBackIcon from '../../../assets/icons/ArrowBackIcon';
import StoreVerification from '../StoreVerification';
import PickTourItem from './PickTourItem';
import PickTourWithErrorItem from './PickTourWithErrorItem';
import StyledAccordion from '../../layout/StyledAccordion/StyledAccordion';

import {
  createPickTour,
  getPickTourItems,
  printRecallReport,
  resumePickTourWithOrderSelection,
} from '../../../redux/actions/activePickTour.actions';
import { getExternalConfigValues } from '../../../redux/actions/config.actions';
import {
  getOrders,
  setHasManualOverstockOrder,
  clearSelected as clearSelectedAction,
  setOrders,
  setPickTourWithError,
} from '../../../redux/actions/orders.actions';

import useVerifyPickTour from '../../../hooks/useVerifyPickTour';
import useVerifyOrderItem from '../../../hooks/useVerifyOrderItem';
import useWindowSize from '../../../hooks/useWindowSize';

import {
  getOrdersFromState,
  determineIsLoadingFromState,
  selectIsGetOrdersLoading,
  selectPickerStore,
  selectRefreshOrdersShortInterval,
  selectRefreshOrdersLongInterval,
  selectIsOverstockEnabled,
  selectCurrentLanguage,
  getConfigAuthValuesFromState,
  getEmployee,
  isHubLabelMobilePrintingEnabled,
  getActiveToastFromState,
  isEditOrderEnabled,
} from '../../../redux/selectors';
import {
  getSortOrder,
  mergeClassNames,
  padTo2LeadingZeroes,
  padTo3LeadingZeroes,
  setInputIfDigits,
} from '../../../utils';
import { isBundledCommercialWithEditOrder } from '../../../utils/pickHelpers';

import {
  MQ_MAX_WIDTH,
  MQ_MIN_WIDTH,
  NO_PENDING_ORDERS,
  NO_STORE_ID,
  ORDER_ID_OVERSTOCK_ORDER_GENERATE_PCKTOUR,
  ORDER_ID_OVERSTOCK_ORDER_GENERATE_MANUAL_PCKTOUR,
  ORDER_TYPE_OVERSTOCK,
  ORDER_TYPE_RECALL,
  API_FETCH_PENDING,
  SHOULD_UPDATE_PICK_TOUR_SUB_STATUS,
  FALSE,
  TRUE,
  MOBILE_PRINTER_NUMBER,
  ORDER_TYPE_COMMERCIAL,
  ORDER_TYPE_COMMERCIAL_EP,
  APP_PAGE_PICK_TOUR_WITH_ERROR,
  LANGUAGE_SPANISH,
  DUPLICATE_PICK_TOUR_ERROR,
  ORDER_BEING_EDITED_ERROR,
  FRESH_ORDER_BEING_EDITED_ERROR,
} from '../../../constants';

import { Order, CompletedRecallTour, NavMenuTab, OrderType } from '../../../types';
import { APIPickTour } from '../../../redux/sagas/types';

import styles from './OrderList.css';
import ConfirmationScreen from '../../layout/ConfirmationScreen';
import ConfirmationModal from '../../layout/ConfirmationModal';
import ManualEntryScreen from '../../layout/ManualEntryScreen';

import { createToast, removeAllToast } from '../../../redux/actions/toasts.actions';
import { setAppPage } from '../../../redux/actions/appPage.actions';
import RefreshButton from '../../buttons/RefreshButton';
import XIcon from '../../../assets/icons/XIcon';
import trimUserName from '../../../utils/trimUserName';
import RecallHistory from './RecallHistory';
import CompletedEPTourList from './CompletedEPTourList';
import ResetPickTour from './ResetPickTour';
import MobilePrinterModal from '../../layout/MobilePrinterModal';
import MobilePrinter from './MobilePrinter';
import { getGroupDispatchStatus } from '../../../utils/dispatchTime';
import ReturnWithArrowIcon from '../../returnIconButton/ReturnIconButton';
import { getMobilePrinterListFeatureEnabled } from '../../../utils/mobilePrinter';
import { getMobilePrinterList } from '../../../redux/actions/mobilePrinter.actions';

const determineIsOrderSelected = (params: { order: Order; selectedOrders: Order[] }) =>
  params.selectedOrders.some(selectedOrder => selectedOrder.id === params.order.id);

const OrderList = (): ReactElement => {
  const [activeNavTab, setActiveNavTab] = useState<NavMenuTab>(null);
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const [storeInputValue, setStoreInputValue] = useState('');
  const [firstStoreID, setFirstStoreID] = useState('');
  const [needsAcknowledgement, setNeedsAcknowledgement] = useState(false);
  const [shouldShowPrintMenu, setShouldShowPrintMenu] = useState(false);
  const [containerInputValue, setContainerInputValue] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedPickTour, setSelectedPickTour] = useState<APIPickTour | null>(null);
  const [selectedErrorPickTour, setSelectedErrorPickTour] = useState<APIPickTour | null>(null);
  const [selectedCompletedRecallTour, setSelectedCompletedRecallTour] = useState<CompletedRecallTour | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState('');
  const selectedPickTourOrderIDsRef = useRef<Map<string, string[]>>(new Map());

  const refreshOrdersShortInterval = useSelector(selectRefreshOrdersShortInterval);
  const refreshOrdersLongInterval = useSelector(selectRefreshOrdersLongInterval);
  const isOverstockEnabled = useSelector(selectIsOverstockEnabled);
  const isLoading = useSelector(determineIsLoadingFromState);
  const isGetOrdersLoading = useSelector(selectIsGetOrdersLoading);
  const ordersState = useSelector(getOrdersFromState);
  const pickerStore = useSelector(selectPickerStore);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const employee = useSelector(getEmployee);
  const configAuthValues = useSelector(getConfigAuthValuesFromState);
  const isMobileLabelPrintingEnabled = useSelector(isHubLabelMobilePrintingEnabled);
  const activeToast = useSelector(getActiveToastFromState);
  const editOrderEnabled = useSelector(isEditOrderEnabled);

  const verifyPickTour = useVerifyPickTour();
  const verifyOrderItem = useVerifyOrderItem();
  const { innerWidth } = useWindowSize();

  const { hasManualOverstockOrder, ordersByType } = ordersState;
  const isReprintEPLabelActive = activeNavTab === 'reprintEPLabel';
  const isMobilePrinterNavActive = activeNavTab === 'mobilePrinter';
  const isResetPickTourActive = activeNavTab === 'resetPickTour';
  const dispatch = useDispatch();

  const refreshOrdersShortTimer = useRef<number>();
  const refreshOrdersLongTimer = useRef<number>();
  const languageOnOpenRef = useRef<string>(currentLanguage);

  const { t } = useTranslation();
  const translationRoot = 'ORDER_LIST';

  const mobilePrinterNumber = localStorage.getItem(MOBILE_PRINTER_NUMBER);

  const {
    storeID: pickerStoreID,
    shouldPromptStoreSelection,
    shouldPromptStoreVerification,
    storeToVerify,
  } = pickerStore;

  const isAnyOrderSelected =
    selectedOrders.length > 0 ||
    selectedPickTour !== null ||
    selectedErrorPickTour !== null ||
    selectedCompletedRecallTour !== null;

  const currentDate = new Date();

  const overStockOrder = ordersByType?.OVERSTOCK?.orders.find(
    order =>
      order.id === ORDER_ID_OVERSTOCK_ORDER_GENERATE_PCKTOUR ||
      order.id === ORDER_ID_OVERSTOCK_ORDER_GENERATE_MANUAL_PCKTOUR
  );

  const overstockPlaceholder: Order | undefined =
    hasManualOverstockOrder && pickerStoreID
      ? {
          id: ORDER_ID_OVERSTOCK_ORDER_GENERATE_MANUAL_PCKTOUR,
          type: ORDER_TYPE_OVERSTOCK,
          deliveryMethod: null,
          destination: pickerStoreID,
          quantity: null,
          finalizationErrorCode: 0,
          finalizationErrorMessage: null,
          isRetryEnabled: false,
          isUpcScanRequired: true,
          date: `${currentDate.getUTCFullYear()}-${padTo2LeadingZeroes(
            (currentDate.getUTCMonth() + 1).toString()
          )}-${padTo2LeadingZeroes(currentDate.getUTCDate().toString())}T${padTo2LeadingZeroes(
            currentDate.getUTCHours().toString()
          )}:${padTo2LeadingZeroes(currentDate.getUTCMinutes().toString())}:${padTo2LeadingZeroes(
            currentDate.getUTCSeconds().toString()
          )}.${padTo3LeadingZeroes(currentDate.getUTCMilliseconds().toString())}000Z`,
          sequenceNumber: null,
          recallDescription: null,
          recallType: null,
          supplierID: null,
          dispatchTime: null,
        }
      : overStockOrder;

  const allOrdersByTypes = overstockPlaceholder
    ? {
        ...ordersByType,
        OVERSTOCK: {
          orders: [overstockPlaceholder],
          pickTours: [],
          errorPickTours: [],
          sortOrder: ordersByType.OVERSTOCK?.sortOrder || 4,
        },
      }
    : ordersByType;

  const hasOrderGroups = Object.keys(allOrdersByTypes).length;
  const hasOverStock = ORDER_TYPE_OVERSTOCK in allOrdersByTypes;

  const noOrdersMessage = pickerStoreID === null ? NO_STORE_ID : NO_PENDING_ORDERS;

  const clearSelected = () => {
    setSelectedOrders([]);
    setSelectedPickTour(null);
    setSelectedErrorPickTour(null);
    setSelectedCompletedRecallTour(null);
    setShouldShowPrintMenu(false);
  };

  const clearAndFetchOrders = useCallback(() => {
    if (pickerStoreID) {
      clearSelected();
      dispatch(getOrders({ fulfillmentStoreID: pickerStoreID, refreshOrders: true }));
    }
  }, [pickerStoreID, dispatch]);

  const handleNavMenuClose = () => {
    setActiveNavTab(null);
    if (languageOnOpenRef.current !== currentLanguage) {
      clearAndFetchOrders();
    }
  };

  const onCreatePickTour = () => {
    if (selectedPickTour) {
      // Check if this is Edit Order scenario BEFORE setting localStorage flag
      const isSelectiveResumeEnabled = isBundledCommercialWithEditOrder(selectedPickTour, editOrderEnabled);

      // Only set localStorage flag for regular resume flow (not Edit Order flow)
      if (!isSelectiveResumeEnabled) {
        localStorage.setItem(SHOULD_UPDATE_PICK_TOUR_SUB_STATUS, TRUE);
      }

      verifyPickTour.verify(
        pickerStore.storeID,
        configAuthValues,
        selectedPickTour,
        () => {
          const updatedPickTour = { ...selectedPickTour };
          const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
          // eslint-disable-next-line no-param-reassign
          updatedPickTour.assignedPickerEmployeeID = userData.sub || '';

          if (isSelectiveResumeEnabled) {
            const selectedOrderIDs = selectedPickTourOrderIDsRef.current.get(selectedPickTour.pickTourID);
            if (selectedOrderIDs && selectedOrderIDs.length > 0) {
              const filteredOrdersSummaryData = updatedPickTour.metadata.ordersSummaryData.filter(order =>
                selectedOrderIDs.includes(order.orderID)
              );

              // Recalculate metadata totals based on selected orders
              const totalItemsForSelectedOrders = filteredOrdersSummaryData.reduce((sum, order) => sum + order.qty, 0);
              const totalItemsPickedForSelectedOrders = filteredOrdersSummaryData.reduce(
                (sum, order) => sum + order.pickedQty,
                0
              );
              const totalItemsSkippedForSelectedOrders = filteredOrdersSummaryData.reduce(
                (sum, order) => sum + order.skippedQty,
                0
              );
              const unhandledItemsRemainingForSelectedOrders =
                totalItemsForSelectedOrders - totalItemsPickedForSelectedOrders - totalItemsSkippedForSelectedOrders;

              updatedPickTour.metadata = {
                ...updatedPickTour.metadata,
                ordersSummaryData: filteredOrdersSummaryData,
                totalItems: totalItemsForSelectedOrders,
                totalItemsPicked: totalItemsPickedForSelectedOrders,
                totalItemsSkipped: totalItemsSkippedForSelectedOrders,
                unhandledItemsRemaining: unhandledItemsRemainingForSelectedOrders,
              };

              // Edit Order flow: Status update is handled by saga, so no localStorage flag needed
              // Saga will update pick tour status to PROGRESS with filtered order metadata, then fetch items
              dispatch(
                resumePickTourWithOrderSelection({
                  pickTourData: updatedPickTour,
                  pickTourType: selectedPickTour.pickTourType,
                  selectedOrderIDs,
                })
              );
              return; // Exit early
            }
          }

          // Regular resume flow: Fetch items first, then ActivePickTour updates status to PROGRESS
          // localStorage flag signals ActivePickTour to update pick tour status from PAUSED to PROGRESS
          localStorage.setItem(SHOULD_UPDATE_PICK_TOUR_SUB_STATUS, TRUE);
          dispatch(
            getPickTourItems({
              isPickTourCreation: true,
              page: 0,
              pickTourData: updatedPickTour,
              pickTourType: selectedPickTour.pickTourType,
            })
          );
        },
        () => {
          clearAndFetchOrders();
        }
      );
    } else if (selectedErrorPickTour) {
      dispatch(
        setPickTourWithError({
          errorPickTour: selectedErrorPickTour,
        })
      );
      dispatch(setAppPage({ name: APP_PAGE_PICK_TOUR_WITH_ERROR }));
    } else if (selectedOrders[0].type === ORDER_TYPE_RECALL && selectedOrders[0].recallType === 'EXCESS') {
      setNeedsAcknowledgement(true);
    } else {
      verifyOrderItem.verify(
        pickerStore.storeID,
        configAuthValues,
        selectedOrders,
        () => {
          localStorage.setItem(SHOULD_UPDATE_PICK_TOUR_SUB_STATUS, FALSE);
          dispatch(
            createPickTour({
              orders: selectedOrders.map(order => ({
                id: order.id,
                type: order.type,
                isUpcScanRequired: order.isUpcScanRequired,
                supplierID: pickerStoreID,
              })),
            })
          );
        },
        () => {
          clearSelected();
        }
      );
    }
  };

  const handleOrderSelectionChange = useCallback((pickTourID: string, selectedOrderIDs: string[]) => {
    selectedPickTourOrderIDsRef.current.set(pickTourID, selectedOrderIDs);
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    onCreatePickTour();
  };

  const handleModalSubmit = () => {
    setModalMessage('');
    clearAndFetchOrders();
  };

  useEffect(() => {
    if (pickerStoreID) {
      clearSelected();
      dispatch(getExternalConfigValues({ storeID: pickerStoreID }));
    }
    dispatch(setHasManualOverstockOrder({ hasManualOverstockOrder: false }));
  }, [pickerStoreID, clearAndFetchOrders, dispatch]);

  useEffect(() => {
    if (!hasManualOverstockOrder) {
      setSelectedOrders(prevSelectedOrders =>
        prevSelectedOrders.filter(
          prevSelectedOrder => prevSelectedOrder.id !== ORDER_ID_OVERSTOCK_ORDER_GENERATE_MANUAL_PCKTOUR
        )
      );
    }
  }, [hasManualOverstockOrder]);

  useEffect(() => {
    if (!isGetOrdersLoading) {
      if (
        selectedOrders.length < 1 &&
        selectedPickTour === null &&
        selectedErrorPickTour === null &&
        activeNavTab !== 'recallsHistory' &&
        !isReprintEPLabelActive &&
        !isMobilePrinterNavActive &&
        !isResetPickTourActive
      ) {
        if (refreshOrdersLongTimer.current) {
          window.clearTimeout(refreshOrdersShortTimer.current);
          refreshOrdersLongTimer.current = 0;
        }
        // Start the short timer
        refreshOrdersShortTimer.current = window.setTimeout(() => {
          clearAndFetchOrders();
        }, refreshOrdersShortInterval);
      } else {
        // Cancel the short timer
        if (refreshOrdersShortTimer.current) {
          window.clearTimeout(refreshOrdersShortTimer.current);
          refreshOrdersShortTimer.current = 0;
        }
        // Start the long timer
        refreshOrdersLongTimer.current = window.setTimeout(() => {
          clearAndFetchOrders();
        }, refreshOrdersLongInterval);
      }
    }

    return () => {
      if (refreshOrdersShortTimer.current) {
        window.clearTimeout(refreshOrdersShortTimer.current);
      }
      if (refreshOrdersLongTimer.current) {
        window.clearTimeout(refreshOrdersLongTimer.current);
      }
    };
  }, [
    refreshOrdersShortInterval,
    refreshOrdersLongInterval,
    isGetOrdersLoading,
    pickerStoreID,
    selectedOrders,
    dispatch,
    clearAndFetchOrders,
    selectedPickTour,
    selectedErrorPickTour,
    activeNavTab,
    isReprintEPLabelActive,
    isMobilePrinterNavActive,
    isResetPickTourActive,
  ]);

  // handle clear selected and remove overstock order after 503
  useEffect(() => {
    if (ordersState.clearSelected === true) {
      dispatch(clearSelectedAction(false));
      const selected = selectedOrders.map(order => order.id);
      if (selected.some(order => order === ORDER_ID_OVERSTOCK_ORDER_GENERATE_MANUAL_PCKTOUR))
        dispatch(setHasManualOverstockOrder({ hasManualOverstockOrder: false }));

      const newOrdersByType = { ...ordersState.ordersByType };
      Object.keys(newOrdersByType).forEach(orderType => {
        const group = newOrdersByType[orderType as OrderType];
        if (group && group.orders) {
          group.orders = group.orders.filter(order => !selected.includes(order.id));
        }
      });
      dispatch(setOrders({ ordersByType: newOrdersByType }));
      clearSelected();
    }
  }, [dispatch, ordersState, ordersState.clearSelected, selectedOrders]);

  useEffect(() => {
    if (!selectedOrders.length && !selectedPickTour && !selectedErrorPickTour) {
      setSelectedGroup(null);
    }
  }, [selectedOrders, selectedPickTour, selectedErrorPickTour]);

  useEffect(() => {
    const mobilePrinterListFeatureEnabled = getMobilePrinterListFeatureEnabled();

    if (mobilePrinterListFeatureEnabled === null && pickerStore.storeID) {
      dispatch(getMobilePrinterList({ storeId: pickerStore.storeID }));
    }
  }, [pickerStore.storeID, dispatch]);

  useEffect(() => {
    if (modalMessage || !activeToast?.error?.code) return;
    const { code, detail } = activeToast.error;
    // Use error detail for specific nested errors, otherwise use error code
    const errorKey = detail === ORDER_BEING_EDITED_ERROR || detail === FRESH_ORDER_BEING_EDITED_ERROR ? detail : code;
    const errorMessages: Record<string, string> = {
      [DUPLICATE_PICK_TOUR_ERROR]: t('ERRORS.DUPLICATE_PICK_TOUR'),
      [ORDER_BEING_EDITED_ERROR]: t('ERRORS.ORDER_BEING_EDITED'),
      [FRESH_ORDER_BEING_EDITED_ERROR]: t('ERRORS.FRESH_ORDER_BEING_EDITED'),
    };
    const errorMessage = errorMessages[errorKey];
    if (errorMessage) {
      dispatch(removeAllToast());
      setModalMessage(errorMessage);
    }
  }, [activeToast, dispatch, modalMessage, t]);

  const getCreateTourItemCount = (): string | number => {
    // Overstock orders have unknown quantities
    if (selectedOrders.length > 0 && selectedOrders[0].type === 'OVERSTOCK') {
      return t(`${translationRoot}.LIST_ITEM.TBD`);
    }
    // Resuming a paused pick tour
    if (selectedPickTour) {
      return selectedPickTour.metadata.unhandledItemsRemaining;
    }
    // Creating new tour from selected orders
    return selectedOrders.reduce((acc, order) => acc + (order.quantity || 0), 0);
  };

  const footerButtons: ReactElement = (
    <>
      <Button
        disabled={!isAnyOrderSelected}
        color="secondary"
        onClick={() => {
          clearSelected();
        }}
        data-cy="clear-button"
      >
        {t(`${translationRoot}.BUTTONS.CLEAR`)}
      </Button>
      {activeNavTab === 'recallsHistory' ? (
        <Button
          disabled={!isAnyOrderSelected}
          onClick={() => {
            setShouldShowPrintMenu(true);
          }}
          data-cy="create-tour-button"
        >
          {t(`${translationRoot}.BUTTONS.REPRINT`)}
        </Button>
      ) : (
        <Button
          disabled={
            !isAnyOrderSelected ||
            verifyOrderItem.status.status === API_FETCH_PENDING ||
            verifyPickTour.status.status === API_FETCH_PENDING
          }
          onClick={() => {
            if (
              mobilePrinterNumber === null &&
              isMobileLabelPrintingEnabled &&
              selectedGroup === ORDER_TYPE_COMMERCIAL_EP &&
              !selectedErrorPickTour
            ) {
              setIsModalOpen(true);
            } else {
              onCreatePickTour();
            }
          }}
          data-cy="create-tour-button"
        >
          {selectedErrorPickTour
            ? t(`${translationRoot}.BUTTONS.VIEW_ERROR`)
            : `${t(`${translationRoot}.BUTTONS.CREATE`)} (${getCreateTourItemCount()})`}
        </Button>
      )}
    </>
  );

  const handleCloseButton = () => {
    if (selectedCompletedRecallTour) {
      setActiveNavTab('recallsHistory');
    } else {
      clearAndFetchOrders();
      setActiveNavTab(null);
    }
    setSelectedCompletedRecallTour(null);
    setShouldShowPrintMenu(false);
  };

  const groupedOrderSection = () => {
    // Build list of groups to display - always include Commercial, only include others if they have orders or are selected
    let selectedGroupOrders: string[];
    if (selectedGroup) {
      selectedGroupOrders = [selectedGroup];
    } else {
      const allGroups = Object.keys(allOrdersByTypes).sort(
        (a, b) => getSortOrder(a, allOrdersByTypes) - getSortOrder(b, allOrdersByTypes)
      );

      // Determine which group has selected orders (if any)
      const groupWithSelection = selectedOrders.length > 0 ? selectedOrders[0].type : null;

      // Always include COMMERCIAL, filter others based on whether they have orders or have a selection
      selectedGroupOrders = allGroups.filter(group => {
        if (group === ORDER_TYPE_COMMERCIAL) return true; // Always show Commercial
        if (group === groupWithSelection) return true; // Always show group with selected orders
        const { orders = [], pickTours = [], errorPickTours = [] } = allOrdersByTypes[group as OrderType] || {};
        return orders.length > 0 || pickTours.length > 0 || errorPickTours.length > 0; // Only show others if they have orders
      });

      // If COMMERCIAL is not in the API response, add it manually
      if (!selectedGroupOrders.includes(ORDER_TYPE_COMMERCIAL)) {
        selectedGroupOrders.unshift(ORDER_TYPE_COMMERCIAL);
      }

      // If there's a group with selection not in the response, add it manually to keep selection visible
      if (groupWithSelection && !selectedGroupOrders.includes(groupWithSelection)) {
        selectedGroupOrders.push(groupWithSelection);
      }
    }

    return (
      <div className={isAnyOrderSelected ? styles.listSelected : styles.listNotSelected}>
        {selectedGroupOrders.map(group => {
          const { orders = [], pickTours = [], errorPickTours = [] } = allOrdersByTypes[group as OrderType] || {};
          let ordersList: Order[] = [];
          if (!selectedPickTour && !selectedErrorPickTour) {
            ordersList = orders.filter(order => order.finalizationErrorCode === 0);
            if (group === ORDER_TYPE_RECALL && selectedOrders.length > 0) {
              const firstSelectedOrder = selectedOrders[0];
              if (firstSelectedOrder?.id) {
                ordersList = ordersList.filter(order => order.id === firstSelectedOrder.id);
              }
            }
          }

          const errorOrders =
            selectedPickTour || selectedOrders.length
              ? []
              : errorPickTours.filter(
                  pickTour => !selectedErrorPickTour || pickTour.pickTourID === selectedErrorPickTour.pickTourID
                );

          const pickTourList =
            selectedErrorPickTour || selectedOrders.length
              ? []
              : pickTours.filter(pickTour => !selectedPickTour || pickTour.pickTourID === selectedPickTour.pickTourID);

          const groupCount = orders.length + pickTours.length + errorPickTours.length;

          // Determine which group has selected orders
          const groupWithSelection = selectedOrders.length > 0 ? selectedOrders[0].type : null;

          let groupDispatchStatus = '';
          if (group === ORDER_TYPE_COMMERCIAL_EP) {
            groupDispatchStatus = getGroupDispatchStatus(ordersList, pickTourList);
          }

          let accordionSummaryRootClass = '';
          if (groupDispatchStatus === 'overdue' || groupDispatchStatus === 'due-soon') {
            accordionSummaryRootClass = styles.dispatchTimeAlert;
          }

          return (
            <StyledAccordion
              key={group}
              header={
                <h2 className={styles.accordionHeader}>
                  {t(`UTILS.${group}`)}
                  {groupCount ? <span className={styles.headerCount}>({groupCount})</span> : null}
                </h2>
              }
              defaultExpanded={
                group === ORDER_TYPE_COMMERCIAL || group === groupWithSelection || group === selectedGroup
              }
              {...(group === ORDER_TYPE_COMMERCIAL_EP && {
                accordionSummaryRootClass,
              })}
            >
              <ul>
                {groupCount === 0 && group === ORDER_TYPE_COMMERCIAL && (
                  <li className={styles.noCommercialOrders}>
                    <p>{t(`${translationRoot}.NO_COMMERCIAL_ORDERS`)}</p>
                  </li>
                )}
                {errorOrders.map(pickTour => (
                  <PickTourWithErrorItem
                    key={pickTour.pickTourID}
                    pickTour={pickTour}
                    isSelected={selectedErrorPickTour?.pickTourID === pickTour.pickTourID}
                    onSelect={() => {
                      setSelectedErrorPickTour(prev => (prev?.pickTourID === pickTour.pickTourID ? null : pickTour));
                      setSelectedGroup(group);
                    }}
                  />
                ))}

                {pickTourList.map(pickTour => {
                  const isSelectiveResumeEnabled = isBundledCommercialWithEditOrder(pickTour, editOrderEnabled);

                  return (
                    <PickTourItem
                      key={pickTour.pickTourID}
                      pickTour={pickTour}
                      isSelected={selectedPickTour?.pickTourID === pickTour.pickTourID}
                      verifying={
                        verifyPickTour.status.status === API_FETCH_PENDING &&
                        verifyPickTour.status.pickTourID === pickTour.pickTourID
                      }
                      onSelect={() => {
                        if (verifyPickTour.status.status !== API_FETCH_PENDING) {
                          setSelectedPickTour(prev => (prev?.pickTourID === pickTour.pickTourID ? null : pickTour));
                          setSelectedGroup(group);
                        }
                      }}
                      currentLanguage={currentLanguage}
                      editOrderEnabled={editOrderEnabled}
                      onOrderSelectionChange={isSelectiveResumeEnabled ? handleOrderSelectionChange : undefined}
                      pickTourID={pickTour.pickTourID}
                    />
                  );
                })}

                {ordersList.map(order => (
                  <OrderItem
                    key={`${order.id}-${order.sequenceNumber}`}
                    order={order}
                    isSelected={determineIsOrderSelected({ order, selectedOrders })}
                    verifying={
                      verifyOrderItem.status.status === API_FETCH_PENDING &&
                      verifyOrderItem.status.ids.includes(order.id)
                    }
                    onSelect={() => {
                      setSelectedOrders(prevSelectedOrders => {
                        const updated = determineIsOrderSelected({ order, selectedOrders: prevSelectedOrders })
                          ? prevSelectedOrders.filter(o => o.id !== order.id)
                          : [...prevSelectedOrders, order];
                        setSelectedGroup(group);
                        return updated;
                      });
                    }}
                    currentLanguage={currentLanguage}
                  />
                ))}
              </ul>
            </StyledAccordion>
          );
        })}
      </div>
    );
  };

  const createOverstockPlaceholder = () => {
    dispatch(setHasManualOverstockOrder({ hasManualOverstockOrder: true }));
    setActiveNavTab(null);
  };

  const renderMainContent = () => {
    if (shouldPromptStoreVerification) {
      return <StoreVerification storeToVerify={storeToVerify} />;
    }

    if (shouldPromptStoreSelection) {
      return (
        <StoreLocation
          storeInputValue={storeInputValue}
          firstStoreID={firstStoreID}
          setStoreInputValue={setStoreInputValue}
          setFirstStoreID={setFirstStoreID}
        />
      );
    }

    if (needsAcknowledgement) {
      return (
        <ConfirmationScreen
          confirmationText={`${t(`${translationRoot}.RECALL_ACKNOWLEDGEMENT`)}`}
          confirmationButtonText={`${t(`${translationRoot}.RECALL_UNDERSTAND`)}`}
          handleConfirm={() =>
            dispatch(
              createPickTour({
                orders: selectedOrders.map(order => ({
                  id: order.id,
                  type: order.type,
                  isUpcScanRequired: order.isUpcScanRequired,
                  supplierID: pickerStoreID,
                })),
              })
            )
          }
          backButtonText={`${t(`${translationRoot}.RECALL_BACK`)}`}
          handleBack={() => setNeedsAcknowledgement(false)}
        />
      );
    }

    if (shouldShowPrintMenu) {
      return (
        <ManualEntryScreen
          buttonText={t(`ACTIVE_PICK_TOUR.DETAILS_VIEW.CONFIRM`)}
          disabled={containerInputValue === ''}
          inputValue={containerInputValue}
          type="number"
          handleInputChange={value => setInputIfDigits({ inputSetter: setContainerInputValue, value })}
          label={t(`ACTIVE_PICK_TOUR.DETAILS_VIEW.QTY_INPUT_MOBILE.PLACEHOLDER`)}
          memo={t(`ACTIVE_PICK_TOUR.DETAILS_VIEW.PLEASE_ENTER_CONTAINERS`)}
          placeholder={t(`ACTIVE_PICK_TOUR.DETAILS_VIEW.QTY_INPUT_MOBILE.PLACEHOLDER`)}
          additionalMemo={t(`ACTIVE_PICK_TOUR.DETAILS_VIEW.RECALL_STICKER_WARNING_MESSAGE`)}
          handleSubmit={() => {
            if (selectedCompletedRecallTour && parseInt(containerInputValue, 10) > 0) {
              setShouldShowPrintMenu(false);
              dispatch(
                printRecallReport({
                  pickTourId: selectedCompletedRecallTour.recallPickTourId,
                  labelQty: parseInt(containerInputValue, 10),
                })
              );
            } else if (
              parseInt(containerInputValue, 10) === 0 &&
              selectedCompletedRecallTour &&
              parseInt(`${selectedCompletedRecallTour.totalQtyPicked}`, 10) === 0
            ) {
              setShouldShowPrintMenu(false);
            } else {
              dispatch(
                createToast({
                  type: 'error',
                  message: t(`ACTIVE_PICK_TOUR.DETAILS_VIEW.LABEL_VALIDATION`),
                })
              );
            }
          }}
        />
      );
    }

    if (isReprintEPLabelActive) {
      return <CompletedEPTourList />;
    }

    if (isResetPickTourActive) {
      return <ResetPickTour />;
    }

    if (isLoading) return null;

    if (activeNavTab === 'recallsHistory') {
      return (
        <RecallHistory
          selectedCompletedRecallTour={selectedCompletedRecallTour}
          setSelectedCompletedRecallTour={setSelectedCompletedRecallTour}
        />
      );
    }

    if (hasOrderGroups) {
      return <>{groupedOrderSection()}</>;
    }

    return (
      <article className={styles.noOrdersContainer}>
        <p>{t(`${translationRoot}.${noOrdersMessage}`)}</p>
      </article>
    );
  };

  return (
    <>
      <Header>
        <Waffle
          activeNavTab={activeNavTab}
          storeInputValue={storeInputValue}
          firstStoreID={firstStoreID}
          hasOverstockOrder={!!overstockPlaceholder || hasOverStock || hasManualOverstockOrder}
          setStoreInputValue={setStoreInputValue}
          setFirstStoreID={setFirstStoreID}
          setActiveNavTab={setActiveNavTab}
          createOverstockPlaceholder={createOverstockPlaceholder}
          isOverstockEnabled={isOverstockEnabled}
          clearSelected={clearSelected}
          clearAndFetchOrders={clearAndFetchOrders}
          languageOnOpenRef={languageOnOpenRef}
        />
        <Logo />
        {activeNavTab === 'generateOverstock' ? (
          <h1 className={styles.pageHeader}>{t(`${translationRoot}.BUTTONS.GENERATE_ORDER_BUTTON`)}</h1>
        ) : (
          <>
            <h1
              className={mergeClassNames(
                styles.pageHeader,
                currentLanguage === LANGUAGE_SPANISH && styles.pageHeaderSpanish
              )}
            >
              {t(`${translationRoot}.PICK`)}
            </h1>
            <span
              className={mergeClassNames(styles.divider, currentLanguage === LANGUAGE_SPANISH && styles.dividerSpanish)}
            />
            {/* TODO: Replace Store with store type if call is made that gets store type */}
            <div>
              <p>
                {pickerStoreID && !shouldPromptStoreVerification
                  ? `${t(`${translationRoot}.LIST_ITEM.STORE`)}#: ${String(Number(pickerStoreID)).padStart(5, '0')}`
                  : `${t(`${translationRoot}.DETECTING`)}`}
              </p>
              <p>{trimUserName(employee.name || '', innerWidth)}</p>
            </div>
          </>
        )}

        {(activeNavTab === null || isResetPickTourActive) && pickerStoreID && (
          <RefreshButton activeNavTab={activeNavTab} modalMessage={modalMessage} />
        )}
        {activeNavTab === 'recallsHistory' && pickerStoreID && (
          <IconButton icon={<XIcon />} onClick={handleCloseButton} />
        )}

        {isReprintEPLabelActive && pickerStoreID && (
          <IconButton icon={<ReturnWithArrowIcon />} onClick={handleCloseButton} data-cy="reprint-ep-return-button" />
        )}

        {isMobilePrinterNavActive && pickerStoreID && (
          <IconButton
            icon={<ReturnWithArrowIcon />}
            onClick={handleCloseButton}
            data-cy="mobile-printer-return-button"
          />
        )}

        {((!shouldPromptStoreSelection && activeNavTab === 'storeLocation') ||
          firstStoreID ||
          activeNavTab === 'generateOverstock') && (
          <IconButton
            icon={<ArrowBackIcon />}
            onClick={() => {
              if (activeNavTab === 'storeLocation') {
                if (firstStoreID) {
                  setStoreInputValue('');
                  setFirstStoreID('');
                } else {
                  setStoreInputValue('');
                  handleNavMenuClose();
                }
              }
              if (activeNavTab === 'generateOverstock') {
                handleNavMenuClose();
              }
            }}
            data-cy="nav-menu-close-button"
          />
        )}
      </Header>
      <Main>
        {isMobilePrinterNavActive ? <MobilePrinter /> : renderMainContent()}
        <MobilePrinterModal isModalOpen={isModalOpen} onModalClose={handleModalClose} />
        {modalMessage && (
          <ConfirmationModal message={modalMessage} buttonTitle={t('OK')} handleSubmit={handleModalSubmit} />
        )}
      </Main>
      {isReprintEPLabelActive || isMobilePrinterNavActive || isResetPickTourActive ? null : (
        <>
          <MediaQuery maxWidth={MQ_MAX_WIDTH}>
            <SlideUpFooter
              className={styles.footer}
              isOpen={
                isAnyOrderSelected &&
                !shouldPromptStoreSelection &&
                !shouldPromptStoreVerification &&
                !needsAcknowledgement &&
                !shouldShowPrintMenu
              }
            >
              {footerButtons}
            </SlideUpFooter>
          </MediaQuery>
          <MediaQuery minWidth={MQ_MIN_WIDTH}>
            {!shouldPromptStoreSelection &&
            !shouldPromptStoreVerification &&
            !shouldShowPrintMenu &&
            !needsAcknowledgement ? (
              <Footer className={styles.footer}>{footerButtons}</Footer>
            ) : null}
          </MediaQuery>
        </>
      )}
    </>
  );
};

export default OrderList;
